import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Loader2,
  Plus,
  Search,
  Trash2,
  Users,
  UserPlus,
  X,
  MoreHorizontal,
  Tag,
} from 'lucide-react';
import { useMe, AuthError, goToLogin } from '@/lib/auth';
import {
  useContacts,
  useContactGroups,
  useCreateGroup,
  useDeleteGroup,
  useAddToGroup,
  useRemoveFromGroup,
  useAddContact,
  type Contact,
} from '@/lib/contacts';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { tagDotClass, tagClasses, TAG_COLORS } from '@/lib/tag-colors';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export const Route = createFileRoute('/contacts')({
  component: Contacts,
});

function Contacts() {
  const { data: me, isLoading, error } = useMe();
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);

  const groupsQ = useContactGroups();
  const contactsQ = useContacts({ query, groupId: activeGroup });
  const addToGroup = useAddToGroup();
  const removeFromGroup = useRemoveFromGroup();
  const deleteGroup = useDeleteGroup();

  const groups = groupsQ.data?.groups ?? [];
  const contacts = contactsQ.data?.contacts ?? [];

  const allSelected = contacts.length > 0 && contacts.every((c) => selected.has(c.email));
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(contacts.map((c) => c.email)));
  }
  function toggleOne(email: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  }

  function bulkAdd(groupId: number, groupName: string) {
    const emails = Array.from(selected);
    if (emails.length === 0) return;
    addToGroup.mutate(
      { groupId, emails },
      {
        onSuccess: () => {
          toast.success(`Added ${emails.length} to ${groupName}`);
          setSelected(new Set());
        },
        onError: () => toast.error('Could not add to group'),
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="size-5 animate-spin" strokeWidth={1.75} />
      </div>
    );
  }
  if (error instanceof AuthError) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="w-full max-w-sm rounded-2xl border bg-card p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold tracking-tight">Sign in to continue</h1>
          <Button onClick={goToLogin} className="mt-6 w-full">
            Sign in with Clougency
          </Button>
        </div>
      </div>
    );
  }
  if (!me) return null;

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-8 md:py-8">
        <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Everyone you correspond with. Group addresses to filter mail by sender.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setAddContactOpen(true)}>
              <UserPlus className="size-4" strokeWidth={1.75} /> Add contact
            </Button>
            <Button size="sm" onClick={() => setNewGroupOpen(true)}>
              <Plus className="size-4" strokeWidth={1.75} /> New group
            </Button>
          </div>
        </header>

        <div className="grid gap-6 md:grid-cols-[220px_1fr]">
          {/* Groups sidebar */}
          <nav className="flex gap-1 overflow-x-auto md:flex-col md:overflow-visible">
            <GroupButton
              active={activeGroup === null}
              onClick={() => setActiveGroup(null)}
              icon={<Users className="size-4" strokeWidth={1.75} />}
              label="All contacts"
            />
            {groups.map((g) => (
              <div key={g.id} className="group/gi flex items-center">
                <GroupButton
                  active={activeGroup === g.id}
                  onClick={() => setActiveGroup(g.id)}
                  icon={<span className={cn('size-2.5 rounded-full', tagDotClass(g.color))} />}
                  label={g.name}
                  count={g.count}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 shrink-0 opacity-0 group-hover/gi:opacity-100"
                      aria-label={`Manage ${g.name}`}
                    >
                      <MoreHorizontal className="size-4" strokeWidth={1.75} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="gap-2 text-destructive focus:text-destructive"
                      onClick={() => {
                        if (confirm(`Delete group "${g.name}"? Contacts are not deleted.`)) {
                          deleteGroup.mutate(g.id, {
                            onSuccess: () => {
                              if (activeGroup === g.id) setActiveGroup(null);
                              toast.success('Group deleted');
                            },
                          });
                        }
                      }}
                    >
                      <Trash2 className="size-4" strokeWidth={1.75} /> Delete group
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </nav>

          {/* Contact list */}
          <div className="min-w-0">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name or email…"
                  className="h-9 w-full rounded-md border bg-transparent pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              {selected.size > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="secondary">
                      <Tag className="size-4" strokeWidth={1.75} /> Add {selected.size} to group…
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Add to group</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {groups.length === 0 && (
                      <DropdownMenuItem disabled>No groups yet</DropdownMenuItem>
                    )}
                    {groups.map((g) => (
                      <DropdownMenuItem key={g.id} onClick={() => bulkAdd(g.id, g.name)} className="gap-2">
                        <span className={cn('size-2.5 rounded-full', tagDotClass(g.color))} />
                        {g.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <div className="overflow-hidden rounded-lg border">
              <div className="flex items-center gap-3 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
                <span className="flex-1">Contact</span>
                <span className="hidden w-28 shrink-0 sm:block">Groups</span>
                <span className="w-14 shrink-0 text-right">Seen</span>
              </div>
              {contactsQ.isLoading && (
                <div className="px-3 py-8 text-center text-sm text-muted-foreground">Loading…</div>
              )}
              {!contactsQ.isLoading && contacts.length === 0 && (
                <div className="px-3 py-10 text-center text-sm text-muted-foreground">
                  {query || activeGroup != null ? 'No matching contacts.' : 'No contacts yet.'}
                </div>
              )}
              {contacts.map((c) => (
                <ContactRow
                  key={c.email}
                  contact={c}
                  checked={selected.has(c.email)}
                  onToggle={() => toggleOne(c.email)}
                  onRemoveGroup={(gid, gname) =>
                    removeFromGroup.mutate(
                      { groupId: gid, email: c.email },
                      { onSuccess: () => toast.success(`Removed from ${gname}`) },
                    )
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <NewGroupDialog open={newGroupOpen} onOpenChange={setNewGroupOpen} />
      <AddContactDialog open={addContactOpen} onOpenChange={setAddContactOpen} />
    </div>
  );
}

function GroupButton({
  active,
  onClick,
  icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors',
        active ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
      )}
    >
      <span className="flex size-4 shrink-0 items-center justify-center">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {count != null && <span className="shrink-0 text-xs text-muted-foreground">{count}</span>}
    </button>
  );
}

function ContactRow({
  contact,
  checked,
  onToggle,
  onRemoveGroup,
}: {
  contact: Contact;
  checked: boolean;
  onToggle: () => void;
  onRemoveGroup: (groupId: number, groupName: string) => void;
}) {
  const initial = (contact.name || contact.email).trim().charAt(0).toUpperCase();
  return (
    <div className="flex items-center gap-3 border-b px-3 py-2.5 last:border-b-0 hover:bg-muted/30">
      <Checkbox checked={checked} onCheckedChange={onToggle} aria-label={`Select ${contact.email}`} />
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">
          {contact.name || contact.email}
          {contact.company && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">{contact.company}</span>
          )}
        </div>
        <div className="truncate text-xs text-muted-foreground">{contact.email}</div>
      </div>
      <div className="hidden w-28 shrink-0 flex-wrap gap-1 sm:flex">
        {contact.groups.map((g) => (
          <span
            key={g.id}
            className={cn(
              'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]',
              tagClasses(g.color),
            )}
          >
            {g.name}
            <button
              type="button"
              onClick={() => onRemoveGroup(g.id, g.name)}
              className="opacity-70 hover:opacity-100"
              aria-label={`Remove from ${g.name}`}
            >
              <X className="size-2.5" strokeWidth={2.5} />
            </button>
          </span>
        ))}
      </div>
      <span className="w-14 shrink-0 text-right text-xs text-muted-foreground">{contact.timesSeen}</span>
    </div>
  );
}

function NewGroupDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>('blue');
  const create = useCreateGroup();
  function submit() {
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim(), color },
      {
        onSuccess: () => {
          toast.success(`Group "${name.trim()}" created`);
          setName('');
          onOpenChange(false);
        },
        onError: () => toast.error('Could not create group (duplicate name?)'),
      },
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>New contact group</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder='e.g. "Known Clients"'
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="flex flex-wrap gap-1.5">
            {TAG_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={cn(
                  'size-6 rounded-full ring-offset-2 ring-offset-background',
                  tagDotClass(c),
                  color === c && 'ring-2 ring-ring',
                )}
                aria-label={c}
              />
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending}>
            Create group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddContactDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const add = useAddContact();
  const valid = useMemo(() => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()), [email]);
  function submit() {
    if (!valid) return;
    add.mutate(
      { email: email.trim(), name: name.trim() || undefined },
      {
        onSuccess: () => {
          toast.success('Contact added');
          setEmail('');
          setName('');
          onOpenChange(false);
        },
        onError: () => toast.error('Could not add contact'),
      },
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Add contact</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <input
            autoFocus
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="Name (optional)"
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || add.isPending}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
